import { NgClass } from '@angular/common'
import { Component, ElementRef, OnDestroy, OnInit, computed, effect, inject, output, signal, viewChild } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { OrganizationCardComponent } from '@app/v2/organization-switcher/components/org-card/organization-card.component'
import { OrganizationSwitcherService } from '@app/v2/organization-switcher/services/organization-switcher.service'
import { V2TranslationService } from '@app/v2/services/translation.service'
import { AuthService } from '@auth/services/auth.service'
import { TranslateModule } from '@ngx-translate/core'
import { Workspace } from '@workspaces/interfaces/workspace.interface'
import { Subject, takeUntil } from 'rxjs'

/**
 * Drawer-specific organization collection component
 * Displays organization cards in a mobile-friendly layout with search functionality
 */
@Component({
  selector: 'app-drawer-org-collection',
  standalone: true,
  imports: [FormsModule, TranslateModule, OrganizationCardComponent, NgClass],
  templateUrl: './drawer-org-collection.component.html',
  styleUrls: ['./drawer-org-collection.component.scss']
})
export class DrawerOrgCollectionComponent implements OnInit, OnDestroy {
  private _organizationSwitcherService = inject(OrganizationSwitcherService)
  private _authService = inject(AuthService)
  private _destroy$ = new Subject<void>()
  private _searchDebounceTimer: any

  // Signal-based outputs
  organizationSelected = output<Workspace>()
  closeRequested = output<void>()
  cardClicked = output<Workspace>()
  // Signal-based ViewChild
  scrollContainer = viewChild<ElementRef>('scrollContainer')

  // Signals for reactive state management

  // Computed properties
  public organizations = computed(() => this._organizationSwitcherService.organizations())
  public currentOrganization = computed(() => this._organizationSwitcherService.currentOrganization())
  public isLoading = computed(() => this._organizationSwitcherService.isLoading())
  public error = computed(() => this._organizationSwitcherService.error())
  public currentWorkspaceId = computed(() => this._currentWorkspaceId())
  public searchQuery = computed(() => this._searchQuery())
  public isLoadingMore = computed(() => this._organizationSwitcherService.isLoadingMore())
  public hasMorePages = computed(() => this._organizationSwitcherService.hasMorePages())
  public isSearching = computed(() => this._organizationSwitcherService.isSearching())

  // Use organizations directly from service (no client-side filtering)
  public filteredOrganizations = computed(() => this.organizations())
  public isRTL = computed(() => this._v2TranslationService.currentLang()?.direction === 'rtl')
  public shouldShowLoading = computed(() => this.isLoading() || this.isSearching())
  public shouldShowError = computed(() => this.error() && !this.isLoading() && !this.isSearching())
  private _searchQuery = signal<string>('')
  private _currentWorkspaceId = signal<number | null>(null)
  private _v2TranslationService = inject(V2TranslationService)

  // Effect to handle debounced search
  constructor() {
    effect(() => {
      const query = this._searchQuery()
      // Clear previous timer
      if (this._searchDebounceTimer) {
        clearTimeout(this._searchDebounceTimer)
      }

      // Set new timer for debounced search
      this._searchDebounceTimer = setTimeout(() => {
        if (query) {
          this._performSearch(query)
        } else {
          this.loadOrganizations()
        }
      }, 300)
    })
  }

  public shouldShowNoResults = computed(() => !this.isLoading() && !this.isSearching() && !this.error() && this.filteredOrganizations().length === 0)

  /**
   * Initializes the component
   */
  ngOnInit(): void {
    this._subscribeToWorkspaceChanges()
    this.loadCurrentWorkspaceId()
  }

  /**
   * Cleans up subscriptions on component destroy
   */
  ngOnDestroy(): void {
    this._destroy$.next()
    this._destroy$.complete()
    // Clear search debounce timer
    if (this._searchDebounceTimer) {
      clearTimeout(this._searchDebounceTimer)
    }
  }

  /**
   * Loads current workspace ID from AuthService
   */
  private loadCurrentWorkspaceId(): void {
    // Get current workspace from AuthService
    const currentWorkspace = this._authService.currentWorkspace
    if (currentWorkspace && currentWorkspace.id) {
      this._currentWorkspaceId.set(currentWorkspace.id)
      return
    }

    // Fallback to current parent workspace from AuthService
    const currentParentWorkspace = this._authService.currentParentWorkspace
    if (currentParentWorkspace && currentParentWorkspace.id) {
      this._currentWorkspaceId.set(currentParentWorkspace.id)
      return
    }

    // If no current workspace found, use the first workspace in the list
    this.organizations()[0] && this._currentWorkspaceId.set(this.organizations()[0].id)
  }

  /**
   * Subscribes to workspace changes from AuthService
   * Updates the internal state when workspace changes externally
   */
  private _subscribeToWorkspaceChanges(): void {
    this._authService.currentWorkspaceChange.pipe(takeUntil(this._destroy$)).subscribe((workspace) => {
      // Update the current workspace ID when it changes from external sources
      if (workspace?.id) {
        this._currentWorkspaceId.set(workspace.id)
      }
    })
  }

  /**
   * Loads organizations from the service
   */
  public loadOrganizations(): void {
    this._organizationSwitcherService
      .loadOrganizations()
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: () => {
          // Organizations loaded successfully
        },
        error: (error) => {
          console.error('Failed to load organizations:', error)
        }
      })
  }

  /**
   * Handles scroll events for infinite scrolling
   * @param event - Scroll event
   */
  onScroll(event: Event): void {
    const element = event.target as HTMLElement

    // Check if we're near the bottom of the scroll container
    const scrollTop = element.scrollTop
    const scrollHeight = element.scrollHeight
    const clientHeight = element.clientHeight
    const scrollPercentage = ((scrollTop + clientHeight) / scrollHeight) * 100

    // Trigger when 80% scrolled
    if (scrollPercentage >= 80 && this.hasMorePages() && !this.isLoadingMore() && !this.searchQuery().trim()) {
      this.loadMoreOrganizations()
    }
  }

  /**
   * Loads more organizations for infinite scrolling
   */
  public loadMoreOrganizations(): void {
    if (!this.hasMorePages() || this.isLoadingMore() || this.searchQuery().trim()) {
      return // Don't load more if searching or already loading
    }

    this._organizationSwitcherService
      .loadMoreOrganizations()
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: () => {},
        error: (error) => {
          console.error('Failed to load more organizations:', error)
        }
      })
  }

  /**
   * Handles organization selection
   * @param organization - Selected organization
   */
  public onOrganizationSelected(organization: Workspace): void {
    // Update the current workspace ID immediately for UI feedback
    this._currentWorkspaceId.set(organization.id)

    // Call the API to update the backend
    this._authService.updateLastActiveOrganization(organization).pipe(takeUntil(this._destroy$)).subscribe()

    // Emit both events
    this.organizationSelected.emit(organization)
    this.cardClicked.emit(organization)
  }

  /**
   * Handles search input changes with debouncing (now handled by effect)
   */
  public onSearchInputChange(value: string): void {
    this._searchQuery.set(value)
  }

  /**
   * Handles Enter key press for immediate search
   */
  public onSearchKeyPress(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      const query = this._searchQuery()
      this._performSearch(query)
    }
  }

  /**
   * Clears the search query and resets to show all organizations
   */
  public clearSearch(): void {
    this._searchQuery.set('')
    this.loadOrganizations() // Reload all organizations
  }

  /**
   * Performs the actual search
   * @param query - Search query
   */
  private _performSearch(query: string): void {
    if (query.trim().length >= 3 || query.trim().length === 0) {
      this._organizationSwitcherService
        .searchOrganizations(query)
        .pipe(takeUntil(this._destroy$))
        .subscribe({
          next: () => {
            this.scrollContainer()?.nativeElement?.scrollTo(0, 0)
          },
          error: (err) => {
            console.error('Search failed:', err)
          }
        })
    }
  }

  /**
   * Checks if the given organization is the current one
   * @param organization - Organization to check
   * @returns boolean - True if it's the current organization
   */
  public isCurrentOrganization(organization: Workspace): boolean {
    return this.currentWorkspaceId() === organization.id
  }
}
